import { useTranslation } from './useTranslation';

const getNestedKeys = key => key.split('.');

//=========================== Default fitlers=====================//

const createFilterEntries = filters => {
  return filters.reduce((acc, filter) => {
    const { key, label } = filter;
    const isLabelFromFilterConfig = typeof filter.filterConfig?.label === 'string';
    const prefixedKey = isLabelFromFilterConfig ? `${key}.filterConfig` : key;
    const val = isLabelFromFilterConfig ? filter.filterConfig.label : label;
    acc[prefixedKey] = val;
    return acc;
  }, {});
};

const prepareFilters = (filters, filterEntries) => {
  for (let totalKey in filterEntries) {
    const [key, suffix = null] = getNestedKeys(totalKey);
    const filter = filters.find(f => f.key === key);
    if (!filter) {
      continue;
    }
    if (suffix) {
      filter[suffix].label = filterEntries[totalKey];
    } else {
      filter.label = filterEntries[totalKey];
    }
  }
};

//======================== LIsting Fields======================//
const convertListingFieldsToObj = listingFields => {
  return listingFields.reduce((acc, field) => {
    acc[field.key] = field;
    return acc;
  }, {});
};

const createListingFieldEntries = listingFields => {
  const createSingleEntry = (listingField, result) => {
    const { key, enumOptions, filterConfig, showConfig, saveConfig } = listingField;
    const prefix = key + '.';
    const showConfigLabel = prefix + 'showConfig';
    const filterConfigLabel = prefix + 'filterConfig';
    const saveConfigLabel = prefix + 'saveConfig';
    result[saveConfigLabel] = saveConfig.label;
    result[filterConfigLabel] = filterConfig.label;
    result[showConfigLabel] = showConfig.label;
    enumOptions.reduce((acc, optn) => {
      const key = prefix + optn.option;
      acc[key] = optn.label;
      return acc;
    }, result);
  };
  return listingFields.reduce((acc, listingField) => {
    createSingleEntry(listingField, acc);
    return acc;
  }, {});
};

const prepareListingFields = (listingFieldRef, listingFieldEntries) => {
  const prepare = listingFieldEntries => {
    const listingFields = Object.entries(listingFieldEntries).reduce((acc, entry) => {
      const [entryKey, entryVal] = entry;
      const [prefix, key] = getNestedKeys(entryKey);
      if (acc[prefix] === undefined) {
        acc[prefix] = { enumOptions: [] };
      }
      if (['showConfig', 'filterConfig', 'saveConfig'].includes(key)) {
        listingFieldRef[prefix][key].label = entryVal;
      } else {
        acc[prefix].enumOptions.push({ option: key, label: entryVal });
      }
      return acc;
    }, {});
    return listingFields;
  };
  const merge = listingFields => {
    for (let key in listingFields) {
      Object.assign(listingFieldRef[key], listingFields[key]);
    }
  };
  merge(prepare(listingFieldEntries));
};

//================================== Categories===========================//
const createEntries = (categories, subcategoriesNames) => {
  const prepareCategories = (categories, result, prefix = '') => {
    categories.reduce((acc, category) => {
      categoryEntires(prefix, category, acc);
      return acc;
    }, result);
  };

  const categoryEntires = (prefix, category, res) => {
    const { id, name } = category;
    const subcategories = category[subcategoriesNames] ?? [];
    const prefixedId = prefix + id;
    res[prefixedId] = name;
    prepareCategories(subcategories, res, `${prefixedId}.`);
  };

  const result = {};
  prepareCategories(categories, result);
  return result;
};

const createSubcategories = (keys, value, subCategories = []) => {
  const [key] = keys;
  const mainSubCate = subCategories.find(subCate => subCate.id === key);
  if (mainSubCate === undefined) {
    subCategories.push({ id: key, name: value, subcategories: [] });
  } else {
    createSubcategories(keys.slice(1), value, mainSubCate.subcategories);
  }
  return subCategories;
};

const createCategories = (key, value, res) => {
  const keys = getNestedKeys(key);
  if (keys.length === 1) {
    res[key] = {
      id: key,
      name: value,
      subcategories: [],
    };
    return;
  }
  const [prefix, ...rest] = keys;
  const { subcategories = [] } = res[prefix];
  createSubcategories(rest, value, subcategories);
};

const prepareCategory = categoryEntries => {
  const result = {};
  for (let [key, value] of Object.entries(categoryEntries)) {
    createCategories(key, value, result);
  }
  return Object.values(result);
};

//======================================= HOOKS======================================//

const useCategoryTranslations = appConfig => {
  const cateEntries = createEntries(appConfig.categoryConfiguration.categories, 'subcategories');
  const { translation } = useTranslation({
    batchId: appConfig.categoryConfiguration.key,
    translationEntries: cateEntries,
  });
  const updatedCategories = prepareCategory(translation);
  appConfig.categoryConfiguration.categories = updatedCategories;
};

const useListingFieldsTranslations = appConfig => {
  const { listingFields } = appConfig.listing;
  const listingField = convertListingFieldsToObj(listingFields);
  const listingEntries = createListingFieldEntries(listingFields);
  const { translation } = useTranslation({
    batchId: 'listingFields',
    translationEntries: listingEntries,
  });
  prepareListingFields(listingField, translation);
  appConfig.listing.listingFields = Object.values(listingField);
};

const useFilterTranslations = appConfig => {
  const { defaultFilters } = appConfig.search;
  const validDefaultFilters = defaultFilters.filter(
    filter => filter.key && (filter.label || filter.filterConfig?.label)
  );
  const entries = createFilterEntries(validDefaultFilters);
  const { translation } = useTranslation({
    batchId: 'listingFilters',
    translationEntries: entries,
  });
  prepareFilters(defaultFilters, translation);
};

//========================== FInal Hooks======================//
export function useAppConfigTranslation(appConfig) {
  useCategoryTranslations(appConfig);
  useListingFieldsTranslations(appConfig);
  useFilterTranslations(appConfig);
}
